/**
 * Form controls (§18): a labelled Field wrapper plus token-styled Input and
 * Select. Labels are real <label>s (the control is nested) so clicking the label
 * focuses the control and screen readers announce it. Input/Select forward refs
 * (the queue focuses the filter input on "/").
 */
import { forwardRef, type InputHTMLAttributes, type ReactNode } from "react";

/**
 * Label-over-control wrapper used in the filter bar and forms. `htmlFor`/`id`
 * are optional — when the control isn't a native form element (e.g. the custom
 * Select), render Field with `as="div"` so it doesn't wrap a non-labelable child.
 */
export function Field({
  label,
  children,
  htmlFor,
  hint,
}: {
  label: string;
  children: ReactNode;
  htmlFor?: string;
  /** Optional helper text rendered under the control. */
  hint?: ReactNode;
}) {
  return (
    <div className="field">
      <span className="field__label" id={htmlFor ? `${htmlFor}-label` : undefined}>
        {label}
      </span>
      {children}
      {hint && <p className="field-hint">{hint}</p>}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={`control input${className ? ` ${className}` : ""}`} {...rest} />;
  },
);
